<script setup lang="ts">
const VIDEO_CLOSE_IMG = 'https://resources1.interface003.com/web30/2026/08/25/bc2282f3-8109-4d35-bfbd-34c3bf56b927.png'

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    src?: string
    poster?: string
    title?: string
  }>(),
  {
    src: '',
    poster: '',
    title: 'Video player',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  close: []
  play: []
  pause: []
  ended: []
}>()

const videoElement = ref<HTMLVideoElement | null>(null)
const dialogElement = ref<HTMLElement | null>(null)
const previousBodyOverflow = ref('')
const bodyOverflowLocked = ref(false)

const visible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

const videoSource = computed(() => props.src || '')

watch(
  () => visible.value,
  async (isVisible) => {
    await nextTick()

    if (!videoElement.value) {
      return
    }

    if (!isVisible) {
      videoElement.value.pause()
      videoElement.value.currentTime = 0
      return
    }

    dialogElement.value?.focus()
    try {
      await videoElement.value.play()
    } catch {
      return
    }
  },
)

watch(
  () => videoSource.value,
  async () => {
    await nextTick()

    if (!visible.value || !videoElement.value) {
      return
    }

    videoElement.value.currentTime = 0
    videoElement.value.play().catch(() => {})
  },
)

watch(
  () => visible.value,
  (isVisible) => {
    if (typeof document === 'undefined') {
      return
    }

    if (isVisible) {
      if (!bodyOverflowLocked.value) {
        previousBodyOverflow.value = document.body.style.overflow
        bodyOverflowLocked.value = true
      }
      document.body.style.overflow = 'hidden'
      return
    }

    if (bodyOverflowLocked.value) {
      document.body.style.overflow = previousBodyOverflow.value
      bodyOverflowLocked.value = false
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  if (typeof document !== 'undefined') {
    if (bodyOverflowLocked.value) {
      document.body.style.overflow = previousBodyOverflow.value
    }
    bodyOverflowLocked.value = false
  }
})

function closePlayer() {
  visible.value = false
  emit('close')
}
</script>

<template>
  <ClientOnly>
    <Transition name="common-video-player">
      <div
        ref="dialogElement"
        v-if="visible"
        class="common-video-player fixed inset-0 z-[2000] flex items-center justify-center bg-[rgba(0,0,0,0.7)] p-[16px]"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        tabindex="-1"
        @click.self="closePlayer"
        @keydown.esc.stop="closePlayer"
      >
        <div
          class="common-video-player__content relative aspect-[858/482] w-full max-w-[858px] overflow-hidden rounded-[8px] bg-black"
        >
          <video
            v-if="videoSource"
            ref="videoElement"
            :src="videoSource"
            :poster="poster"
            class="block size-full object-cover"
            autoplay
            controls
            loop
            playsinline
            preload="auto"
            muted
            controlslist="nodownload noplaybackrate"
            disablePictureInPicture
            noplaybackrate
            @play="emit('play')"
            @pause="emit('pause')"
            @ended="emit('ended')"
          ></video>

          <button
            type="button"
            class="common-video-player__close absolute top-[12px] right-[12px] z-[2] flex size-[22px] cursor-pointer items-center justify-center bg-transparent p-0 rtl:right-auto rtl:left-[12px]"
            aria-label="Close video player"
            @click="closePlayer"
          >
            <CommonImage class="size-[22px]" :src="VIDEO_CLOSE_IMG" alt="" />
          </button>
        </div>
      </div>
    </Transition>
  </ClientOnly>
</template>

<style scoped lang="less">
.common-video-player-enter-active,
.common-video-player-leave-active {
  transition: opacity 0.2s ease;
}

.common-video-player-enter-active .common-video-player__content,
.common-video-player-leave-active .common-video-player__content {
  transition: transform 0.2s ease;
}

.common-video-player-enter-from,
.common-video-player-leave-to {
  opacity: 0;
}

.common-video-player-enter-from .common-video-player__content,
.common-video-player-leave-to .common-video-player__content {
  transform: scale(0.96);
}
</style>
